import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { useLanguage } from "../context/LanguageContext";
import { Sparkle } from "./Sparkle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: "",
        email: "",
        story: "",
        custom_request: "",
    });
    const [showCustom, setShowCustom] = useState(false);
    const [loading, setLoading] = useState(false);

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.story.trim()) {
            toast.error("A name, an email, and a story is all I need.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                story: form.story.trim(),
                custom_request: form.custom_request.trim() || null,
            };
            await axios.post(`${API}/contact`, payload);
            toast.success("Letter received. I’ll write back within 48 hours.");
            setForm({ name: "", email: "", story: "", custom_request: "" });
            setShowCustom(false);
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Could not send. Please try again.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative">
            <div className="max-w-[1320px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    {/* Left */}
                    <div className="lg:col-span-5">
                        <div
                            className="font-mono-archive text-[11px] mb-6 inline-flex items-center gap-2"
                            style={{ color: "var(--muted-ink)" }}
                        >
                            <Sparkle size={12} />
                            {t("contact.eyebrow")}
                        </div>
                        <h2
                            className="font-serif-display text-[48px] sm:text-[68px] lg:text-[84px] leading-[0.98]"
                            style={{ color: "var(--ink)" }}
                        >
                            {t("contact.title.a")}
                            <br />
                            {t("contact.title.b")}
                            <span
                                className="italic"
                                style={{ color: "var(--terracotta)" }}
                            >
                                {t("contact.title.c")}.
                            </span>
                        </h2>
                        <p
                            className="mt-8 max-w-[420px] text-[16px] leading-[1.7]"
                            style={{ color: "var(--ink-soft)" }}
                        >
                            {t("contact.intro")}
                        </p>

                        <div
                            className="mt-10 grid grid-cols-1 gap-5 pt-8 max-w-[420px]"
                            style={{}}
                        >
                            {[
                                ["Email", "charlyndewulf@outlook.com"],
                                ["Base", "Belgium · remote-friendly"],
                            ].map(([k, v]) => (
                                <div
                                    key={k}
                                    className="flex items-baseline justify-between gap-6"
                                >
                                    <span
                                        className="font-mono-archive text-[10px]"
                                        style={{ color: "var(--muted-ink)" }}
                                    >
                                        {k}
                                    </span>
                                    <span
                                        className="font-serif-display text-[18px] italic"
                                        style={{ color: "var(--ink)" }}
                                    >
                                        {v}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right - Form */}
                    <div className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="rounded-[28px] p-8 sm:p-12"
                            style={{
                                background: "var(--paper)",
                            }}
                        >
                            <div className="space-y-7">
                                <div>
                                    <label
                                        className="font-mono-archive text-[10px]"
                                        style={{ color: "var(--muted-ink)" }}
                                        htmlFor="name"
                                    >
                                        {t("contact.label.name")}
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        data-testid="contact-name"
                                        value={form.name}
                                        onChange={onChange}
                                        placeholder={t("contact.placeholder.name")}
                                        className="archive-input"
                                        autoComplete="name"
                                    />
                                </div>

                                <div>
                                    <label
                                        className="font-mono-archive text-[10px]"
                                        style={{ color: "var(--muted-ink)" }}
                                        htmlFor="email"
                                    >
                                        {t("contact.label.email")}
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        data-testid="contact-email"
                                        value={form.email}
                                        onChange={onChange}
                                        placeholder={t("contact.placeholder.email")}
                                        className="archive-input"
                                        autoComplete="email"
                                    />
                                </div>

                                <div>
                                    <label
                                        className="font-mono-archive text-[10px]"
                                        style={{ color: "var(--muted-ink)" }}
                                        htmlFor="story"
                                    >
                                        {t("contact.label.story")}
                                    </label>
                                    <textarea
                                        id="story"
                                        name="story"
                                        data-testid="contact-story"
                                        value={form.story}
                                        onChange={onChange}
                                        placeholder={t("contact.placeholder.story")}
                                        className="archive-input archive-textarea"
                                    />
                                </div>

                                <button
                                    type="button"
                                    data-testid="toggle-custom-request"
                                    onClick={() => setShowCustom((s) => !s)}
                                    className="flex items-center gap-2 font-mono-archive text-[11px]"
                                    style={{ color: "var(--ink-soft)" }}
                                >
                                    {showCustom ? (
                                        <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                                    ) : (
                                        <AddRoundedIcon sx={{ fontSize: 16 }} />
                                    )}
                                    {t("contact.custom")}
                                </button>

                                {showCustom && (
                                    <div>
                                        <textarea
                                            name="custom_request"
                                            data-testid="contact-custom"
                                            value={form.custom_request}
                                            onChange={onChange}
                                            placeholder="Budget, timeline, references, anything I should know..."
                                            className="archive-input archive-textarea"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                                <div
                                    className="font-mono-archive text-[10px] inline-flex items-center gap-2"
                                    style={{ color: "var(--muted-ink)" }}
                                >
                                    <Sparkle size={10} />
                                    {t("contact.response")}
                                </div>
                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={loading}
                                    className="pill pill-terracotta"
                                    style={{ opacity: loading ? 0.65 : 1 }}
                                >
                                    {loading ? t("contact.sending") : t("contact.send")}
                                    <span aria-hidden>↗</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};
